import React, { useState, useEffect } from "react"
import Sticky from "react-sticky-el"
import Sort from "@/components/ProductList/v2/Sort/index"
import ShowHide from "@/components/ProductList/v2/ProductListView/StickyNav/showHide"
import useWindowResize from "@/hooks/useWindowResize"
import closeIcon from "@/public/icons/close.svg"
import searchIcon from "@/public/icons/search.svg"
import { FilterBar } from "@kohler-dsm-library/kohler-core"
import { getAnalyticsData } from "@/components/ProductList/v2/Sort/sortAnalytics"
import { profileBrandTypeahead } from "@/store/features/productListSlice"
import {
  removeMavenoidChat,
  pushAnalyticsObjToDataLayer,
  getSortItemBasedOnURL,
} from "@/utils/helper"
import PropTypes from "prop-types"
import { getShowHideAnalyticsDataFilterBar } from "@/components/ProductList/v2/analytics"
import { getFilterBarProps } from "@/utils/product"
import { useDispatch, useSelector } from "react-redux"
import { searchState, setSearchKeyword } from "@/store/features/searchSlice"
import { selectGenericState } from "@/store/features/genericSlice"
import useIsSsr from "@/hooks/useIsSsr"

const StickyNavHeader = ({
  staticTexts = {},
  totalResults = 0,
  showFilter = false,
  sorts,
  selectedSort,
  setShowFilter = () => {},
  handleSort = () => {},
  isSearchTab,
  page,
  type = "",
  searchKeyword = "",
  onSearch,
  pageId,
  showTotal = true,
  isFaq = false,
  brand = "",
  searchDetails = {},
}) => {
  const isSsr = useIsSsr()
  const dispatch = useDispatch()
  const {
    searchDetails: { keyword: stateKey },
  } = useSelector(searchState)

  const {
    globalConfig: {
      apiEndPointData: { map: { searchBrandPlaceholder = "" } = {} } = {},
    } = {},
  } = useSelector(selectGenericState)

  const [navSticky, setNavSticky] = useState(false)
  const [width] = useWindowResize()
  const [updatedKeyWord, setUpdatedKeyWord] = useState(stateKey || "")
  const [typeAheadResults, setTypeAheadResults] = useState([])
  const [isFilterBarSticky, setIsFilterBarSticky] = useState(false)

  useEffect(() => {
    setUpdatedKeyWord(stateKey)
  }, [stateKey])

  const fetchTypeahead = async () => {
    try {
      const response = await Promise.resolve(
        dispatch(profileBrandTypeahead([updatedKeyWord, brand.toLowerCase()]))
      )

      const transformedResults =
        response?.payload?.response?.docs?.map(doc => ({
          label: doc.productName_s,
          value: doc.productName_s,
        })) || []

      setTypeAheadResults(transformedResults)
    } catch (error) {
      console.error("Error fetching typeahead:", error)
    }
  }

  useEffect(() => {
    if (updatedKeyWord?.length > 2) {
      fetchTypeahead()
    }
  }, [updatedKeyWord, brand, dispatch])

  const handleKeydown = e => {
    if (e.key === "Enter") {
      onSearch(e?.target?.value)
    }
  }

  const addAnalyticsForShowHideFilter = showFilter => {
    if (window.innerWidth < 991) {
      removeMavenoidChat()
    }

    const eventInfo = getShowHideAnalyticsDataFilterBar(showFilter, type)
    pushAnalyticsObjToDataLayer(eventInfo)
    setShowFilter(!showFilter)
  }

  const getSortOptionByValue = (sorts, value) => {
    return sorts
      .map(item => ({ ...item, label: item.name, value: item.query }))
      .find(option => option.value === value)
  }

  const optionsPreparation = sorts => {
    const selectedtab = type
    return sorts.map(item => ({
      label: item.name,
      value: item.query,
      trackingAttributes: {
        "data-datalayer-id": "kds-element-filterbar-sort",
        "data-gbh-data-layer": getAnalyticsData(
          item,
          null,
          {
            selectedtab,
            page,
          },
          ""
        ),
      },
    }))
  }

  const handleDatalayer = (item, { type, page }) => {
    const eventInfo = getAnalyticsData(
      item,
      null,
      {
        selectedtab: type,
        page,
      },
      ""
    )
    const eventInfoObj =
      typeof eventInfo === "string" ? JSON.parse(eventInfo) : eventInfo
    pushAnalyticsObjToDataLayer(eventInfoObj)
  }

  const handleSearchOnchange = value => {
    dispatch(setSearchKeyword(value))
    setUpdatedKeyWord(value)
  }

  // Get the filtered count from Redux for the current tab, fallback to totalResults
  const currentTabCount = isSsr
    ? totalResults
    : searchDetails[type]?.count !== undefined
    ? searchDetails[type].count
    : totalResults

  const filterBarProps = getFilterBarProps({
    totalResults: currentTabCount,
    staticTexts,
    updatedKeyWord,
    handleSearchOnchange,
    onSearch,
    typeAheadResults,
    showFilter,
    addAnalyticsForShowHideFilter,
    sorts,
    getSortItemBasedOnURL,
    optionsPreparation,
    handleSort,
    getSortOptionByValue,
    handleDatalayer,
    type,
    page,
    searchPlaceholder: searchBrandPlaceholder,
    isFilterBarSticky,
    setIsFilterBarSticky,
  })

  return (
    <>
      {page === "search" && isSearchTab ? (
        <FilterBar {...filterBarProps} />
      ) : (
        <Sticky
          onFixedToggle={() => setNavSticky(!navSticky)}
          className="product-listing__sticky-nav-wrap"
          stickyStyle={{ zIndex: 9 }}
          stickyClassName={"sticky sticky-plp-listing"}
        >
          <div
            className={`container-fluid product-listing__sticky-nav ${
              navSticky && "product-listing__sticky-nav--shadow"
            }`}
          >
            <div
              className={`product-listing__row ${
                isSearchTab && "search-result__search-flex"
              }`}
            >
              {navSticky && width < 813 && isSearchTab && (
                <div className="search-result__search-wrap">
                  <span>
                    <img
                      src={searchIcon.src}
                      alt="search-icon"
                      onClick={() => onSearch(updatedKeyWord)}
                    />
                  </span>
                  <input
                    type="text"
                    defaultValue={searchKeyword}
                    onKeyDown={e => handleKeydown(e)}
                    onChange={e => setUpdatedKeyWord(e?.target?.value)}
                  />
                  <span onClick={() => onSearch("", false)}>
                    <img src={closeIcon.src} alt="close-icon" />
                  </span>
                </div>
              )}
              <div className="container kf-react-plp-container">
                <div className="product-listing__row">
                  <div
                    className={`product-listing__col product-listing__controls ${
                      isSearchTab && "search-result__sticky-search-pd"
                    }`}
                  >
                    {isSearchTab ? (
                      <h2
                        aria-label={currentTabCount + staticTexts.resultsCaps}
                        className="product-listing__total"
                      >
                        {currentTabCount?.toLocaleString()}{" "}
                        {staticTexts.resultsCaps}
                      </h2>
                    ) : showTotal ? (
                      <h2
                        aria-label={staticTexts?.totalResults}
                        className="product-listing__total"
                      >
                        {currentTabCount} {staticTexts?.results}
                      </h2>
                    ) : (
                      <span></span>
                    )}

                    {navSticky && width > 812 && isSearchTab && (
                      <div className="search-result__sticky-search">
                        <span>
                          <img
                            src={searchIcon.src}
                            alt="search-icon"
                            onClick={() => onSearch(updatedKeyWord)}
                          />
                        </span>
                        <input
                          type="text"
                          defaultValue={searchKeyword}
                          onKeyDown={e => handleKeydown(e)}
                          onChange={e => setUpdatedKeyWord(e?.target?.value)}
                        />
                        <span onClick={() => onSearch("")}>
                          <img src={closeIcon.src} alt="close-icon" />
                        </span>
                      </div>
                    )}
                    {!pageId ? (
                      <section className={`sorting-section ${isFaq && "px-1"}`}>
                        {!isSearchTab && (
                          <span className="product-listing__fillter_sort">
                            {staticTexts?.filterSort}
                          </span>
                        )}

                        <ShowHide
                          showFilter={showFilter}
                          staticTexts={staticTexts}
                          setShowFilter={setShowFilter}
                          isSearchTab={isSearchTab}
                          page={page}
                          type={type}
                          isFaq={isFaq}
                        />
                        <Sort
                          tabIndex="0"
                          data={sorts}
                          selectedSort={selectedSort}
                          texts={staticTexts}
                          onChange={handleSort}
                          page={page}
                          selectedtab={type}
                          isSearch={isSearchTab}
                        />
                      </section>
                    ) : (
                      <></>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Sticky>
      )}
    </>
  )
}

export default StickyNavHeader

StickyNavHeader.propTypes = {
  staticTexts: PropTypes.object,
  totalResults: PropTypes.number,
  showFilter: PropTypes.bool,
  sorts: PropTypes.array,
  selectedSort: PropTypes.any,
  setShowFilter: PropTypes.func,
  handleSort: PropTypes.func,
  isSearchTab: PropTypes.bool,
  page: PropTypes.string,
  type: PropTypes.string,
  searchKeyword: PropTypes.string,
  onSearch: PropTypes.func,
  pageId: PropTypes.any,
  showTotal: PropTypes.bool,
  isFaq: PropTypes.bool,
  brand: PropTypes.string,
  searchDetails: PropTypes.object,
}
